/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * reCAPTCHA token execution retry loop with diagnostics and inspector reporting.
 */

import { AntiBotUnavailableError } from './recaptcha-loader';
import { GRecaptchaEnterprise } from './recaptcha-config';
import { reportRecaptchaToInspector } from './recaptcha-devcockpit';
import { ANTIBOT_LOG, executeInsideReady } from './recaptcha-execute';

const MAX_ATTEMPTS = 2;
const RETRY_DELAY_MS = 250;

/**
 * Builds a structured diagnostics object describing a failed token attempt,
 * including timing, error shape, and truncated environment fingerprints.
 *
 * @param action Action the token was requested for.
 * @param attempt Current attempt number.
 * @param err The thrown error (or unknown value).
 * @param status HTTP-like status extracted from the error.
 * @param tokenStartTime Timestamp when token generation began.
 * @param siteKey Site key (only a prefix is logged).
 * @returns A log-friendly diagnostics record.
 */
function buildDiag(
  action: string, attempt: number, err: unknown, status: number,
  tokenStartTime: number, siteKey: string,
): Record<string, unknown> {
  return {
    action,
    attempt,
    max_attempts: MAX_ATTEMPTS,
    error_message: err instanceof Error ? err.message : String(err),
    error_name: err instanceof Error ? err.name : typeof err,
    error_status: status,
    elapsed_ms: Date.now() - tokenStartTime,
    site_key_prefix: siteKey.slice(0, 10),
    hostname: window.location.hostname,
    user_agent: navigator.userAgent?.slice(0, 100),
  };
}

/**
 * Warns when token generation exceeded 2s, to surface slow anti-bot round-trips.
 *
 * @param action Action the token was requested for.
 * @param attempt Current attempt number.
 * @param executeMs Time spent in the execute call, in milliseconds.
 * @param tokenStartTime Timestamp when token generation began.
 * @param token The generated token (only its length is logged).
 */
function logSlowToken(
  action: string,
  attempt: number,
  executeMs: number,
  tokenStartTime: number,
  token: string,
): void {
  if (executeMs > 2000) {
    console.warn(ANTIBOT_LOG, 'Token generation slow', {
      action,
      attempt,
      execute_ms: executeMs,
      total_ms: Date.now() - tokenStartTime,
      token_length: token?.length ?? 0,
    });
  }
}

/**
 * Handles a failed attempt: logs diagnostics and reports to the inspector. On
 * the final attempt it throws {@link AntiBotUnavailableError}; otherwise it
 * returns so the caller can retry.
 *
 * @param err The thrown error (or unknown value).
 * @param attempt Current attempt number.
 * @param siteKey Site key for inspector reporting.
 * @param action Action the token was requested for.
 * @param tokenStartTime Timestamp when token generation began.
 * @throws {AntiBotUnavailableError} When all attempts are exhausted.
 */
function handleRetryFailure(
  err: unknown, attempt: number, siteKey: string, action: string, tokenStartTime: number,
): void {
  const status = (err as any)?.status ?? 0;
  const diag = buildDiag(action, attempt, err, status, tokenStartTime, siteKey);
  const message = err instanceof Error ? err.message : String(err);

  if (attempt === MAX_ATTEMPTS) {
    console.error(ANTIBOT_LOG, 'Token generation FAILED — all attempts exhausted', diag);
    reportRecaptchaToInspector(siteKey, status || 401, message);
    throw new AntiBotUnavailableError(
      `reCAPTCHA token generation failed after ${MAX_ATTEMPTS} attempts: ${message}`,
    );
  }

  console.warn(ANTIBOT_LOG, `Token generation attempt ${attempt} failed, retrying...`, diag);
  reportRecaptchaToInspector(siteKey, status || 401, message);
}

/**
 * Run the bounded retry loop calling executeInsideReady, returning the token or
 * throwing AntiBotUnavailableError when all attempts are exhausted.
 */
export async function executeWithRetries(
  enterprise: GRecaptchaEnterprise,
  siteKey: string,
  action: string,
  tokenStartTime: number,
): Promise<string> {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const executeStart = Date.now();
      const token = await executeInsideReady(enterprise, siteKey, action);
      logSlowToken(action, attempt, Date.now() - executeStart, tokenStartTime, token);
      reportRecaptchaToInspector(siteKey, 200);
      return token;
    } catch (err) {
      handleRetryFailure(err, attempt, siteKey, action, tokenStartTime);
      await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
    }
  }

  throw new AntiBotUnavailableError('reCAPTCHA token generation failed');
}
